import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContextualHelp from "../../../components/dashboard/ContextualHelp";
import ResponsiveOperationalTable from "../../../components/dashboard/ResponsiveOperationalTable";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { useOutletContext, useParams } from "react-router-dom";
import { erpApi } from "../../../api/erp";
import { ErpEmpty, ErpLoading, ErpPanel, ErpSectionTitle } from "../../../components/dashboard/erp/ErpUi";
import { ACCOUNT_TYPE_LABELS, ERP_BRAND, entityId } from "../../../utils/erp";

const ACCOUNT_FIELD_LIMITS = Object.freeze({
  code: 80,
  name: 160,
  description: 1000,
});

const emptyForm = {
  code: "",
  name: "",
  type: "asset",
  normalSide: "debit",
  active: true,
  description: "",
};

function limitedValue(value, maxLength) {
  return String(value ?? "").slice(0, maxLength);
}

export default function ProjectErpAccountsPage() {
  const { projectId } = useParams();
  const { refreshBootstrap } = useOutletContext();
  const [accounts, setAccounts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await erpApi.listAccounts(projectId);
      setAccounts(data.accounts || []);
    } catch (err) {
      setError(err.message || "No se pudieron cargar las cuentas contables");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (account) => {
    setEditing(account);
    setForm({
      code: account.code || "",
      name: account.name || "",
      type: account.type || "asset",
      normalSide: account.normalSide || "debit",
      active: account.active !== false,
      description: account.description || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    const lengthViolation = [
      ["Código", form.code, ACCOUNT_FIELD_LIMITS.code],
      ["Nombre", form.name, ACCOUNT_FIELD_LIMITS.name],
      ["Descripción", form.description, ACCOUNT_FIELD_LIMITS.description],
    ].find(([, value, maxLength]) => String(value ?? "").length > maxLength);

    if (lengthViolation) {
      const [fieldLabel, , maxLength] = lengthViolation;
      setError(`${fieldLabel} puede tener como máximo ${maxLength} caracteres.`);
      return;
    }
    if (!form.code.trim() || !form.name.trim()) {
      setError("Completá el código y el nombre de la cuenta.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      if (editing) {
        await erpApi.updateAccount(projectId, entityId(editing), {
          code: form.code.toUpperCase(),
          name: form.name,
          description: form.description,
          active: form.active,
        });
      } else {
        await erpApi.createAccount(projectId, {
          ...form,
          code: form.code.toUpperCase(),
        });
      }
      setDialogOpen(false);
      await Promise.all([load(), refreshBootstrap()]);
    } catch (err) {
      setError(err.message || "No se pudo guardar la cuenta");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (account) => {
    if (account.metadata?.source === "cash_register") return;
    try {
      setError("");
      await erpApi.updateAccount(projectId, entityId(account), { active: !account.active });
      await Promise.all([load(), refreshBootstrap()]);
    } catch (err) {
      setError(err.message || "No se pudo actualizar la cuenta");
    }
  };

  return (
    <Stack spacing={2.4}>
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
      <ErpSectionTitle
        title="Plan de cuentas"
        description="Clasificá qué representa cada operación. Las cajas, bancos y billeteras se crean en Cajas y cuentas financieras y WebProfit vincula su cuenta de fondos automáticamente."
        action={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load} sx={{ textTransform: "none", fontWeight: 850 }}>Actualizar</Button>
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate} sx={{ textTransform: "none", fontWeight: 850 }}>Nueva cuenta</Button>
          </Stack>
        }
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -1 }}>
        <ContextualHelp
          compact
          title="Cuenta financiera vs. cuenta contable"
          text="La cuenta financiera dice dónde está la plata; la cuenta contable dice qué representa el movimiento. Al crear Efectivo, un banco o Mercado Pago, WebProfit genera y vincula automáticamente su cuenta contable de fondos."
          bullets={["No vuelvas a crear Santander o Mercado Pago manualmente en el Plan de cuentas.", "Creá cuentas personalizadas para conceptos como publicidad, alquileres, honorarios u otros ingresos/gastos.", "Si una cuenta deja de usarse, desactivala en lugar de borrar su historial."]}
        />
      </Box>

      <ErpPanel>
        {loading ? <ErpLoading label="Cargando plan de cuentas..." /> : !accounts.length ? <ErpEmpty title="Sin cuentas contables" description="Prepará la estructura contable o creá la primera cuenta manual." /> : (
          <TableContainer>
            <ResponsiveOperationalTable>
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Naturaleza</TableCell>
                  <TableCell>Origen</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={entityId(account)} hover>
                    <TableCell sx={{ fontWeight: 950 }}>{account.code}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 850 }}>{account.name}</Typography>
                      {account.description ? <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{account.description}</Typography> : null}
                    </TableCell>
                    <TableCell><Chip size="small" label={ACCOUNT_TYPE_LABELS[account.type] || account.type} /></TableCell>
                    <TableCell>{account.normalSide === "debit" ? "Deudora" : "Acreedora"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={account.metadata?.source === "cash_register" ? "info" : account.system ? "primary" : "default"}
                        label={account.metadata?.source === "cash_register" ? "Cuenta financiera vinculada" : account.system ? "Sistema" : "Personalizada"}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Tooltip title={account.metadata?.source === "cash_register" ? "Se administra desde Cajas y cuentas financieras" : ""} disableHoverListener={account.metadata?.source !== "cash_register"}>
                            <span>
                              <Switch
                                size="small"
                                checked={account.active !== false}
                                disabled={account.metadata?.source === "cash_register"}
                                onChange={account.metadata?.source === "cash_register" ? undefined : () => toggleActive(account)}
                                inputProps={{
                                  "aria-label": account.metadata?.source === "cash_register"
                                    ? `Estado de cuenta financiera vinculada: ${account.active !== false ? "Activa" : "Inactiva"}. Se administra desde Cajas y cuentas financieras.`
                                    : `${account.active !== false ? "Desactivar" : "Activar"} cuenta ${account.name}`,
                                }}
                              />
                            </span>
                          </Tooltip>
                          <Typography variant="body2">{account.active !== false ? "Activa" : "Inactiva"}</Typography>
                        </Stack>
                        {account.metadata?.source === "cash_register" ? (
                          <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                            Se administra desde Cajas y cuentas financieras
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={account.metadata?.source === "cash_register" ? "Esta cuenta se administra desde Cajas y cuentas financieras" : "Editar cuenta"}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={account.metadata?.source === "cash_register"}
                            onClick={() => openEdit(account)}
                          >
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ResponsiveOperationalTable>
          </TableContainer>
        )}
      </ErpPanel>

      <Dialog open={dialogOpen} onClose={saving ? undefined : () => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>{editing ? "Editar cuenta" : "Nueva cuenta contable"}</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Código"
              required
              value={form.code}
              disabled={Boolean(editing?.system)}
              error={form.code.length > ACCOUNT_FIELD_LIMITS.code}
              inputProps={{ maxLength: ACCOUNT_FIELD_LIMITS.code }}
              onChange={(event) => setForm((current) => ({
                ...current,
                code: limitedValue(event.target.value.toUpperCase(), ACCOUNT_FIELD_LIMITS.code),
              }))}
              helperText={form.code.length > ACCOUNT_FIELD_LIMITS.code
                ? `Máximo ${ACCOUNT_FIELD_LIMITS.code} caracteres.`
                : `${editing?.system ? "El código de una cuenta del sistema no puede cambiarse." : "Usá mayúsculas y guiones bajos, por ejemplo GASTOS_PUBLICIDAD."} ${form.code.length}/${ACCOUNT_FIELD_LIMITS.code} caracteres.`}
            />
            <TextField
              label="Nombre"
              required
              value={form.name}
              error={form.name.length > ACCOUNT_FIELD_LIMITS.name}
              inputProps={{ maxLength: ACCOUNT_FIELD_LIMITS.name }}
              onChange={(event) => setForm((current) => ({
                ...current,
                name: limitedValue(event.target.value, ACCOUNT_FIELD_LIMITS.name),
              }))}
              helperText={form.name.length > ACCOUNT_FIELD_LIMITS.name
                ? `Máximo ${ACCOUNT_FIELD_LIMITS.name} caracteres.`
                : `${form.name.length}/${ACCOUNT_FIELD_LIMITS.name} caracteres.`}
            />
            {!editing ? (
              <>
                <TextField select label="Tipo" value={form.type} onChange={(event) => {
                  const type = event.target.value;
                  setForm((current) => ({ ...current, type, normalSide: ["asset", "expense"].includes(type) ? "debit" : "credit" }));
                }}>
                  {Object.entries(ACCOUNT_TYPE_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </TextField>
                <TextField select label="Naturaleza" value={form.normalSide} onChange={(event) => setForm((current) => ({ ...current, normalSide: event.target.value }))}>
                  <MenuItem value="debit">Deudora</MenuItem>
                  <MenuItem value="credit">Acreedora</MenuItem>
                </TextField>
              </>
            ) : null}
            <TextField
              label="Descripción"
              multiline
              minRows={3}
              value={form.description}
              error={form.description.length > ACCOUNT_FIELD_LIMITS.description}
              inputProps={{ maxLength: ACCOUNT_FIELD_LIMITS.description }}
              onChange={(event) => setForm((current) => ({
                ...current,
                description: limitedValue(event.target.value, ACCOUNT_FIELD_LIMITS.description),
              }))}
              helperText={form.description.length > ACCOUNT_FIELD_LIMITS.description
                ? `Máximo ${ACCOUNT_FIELD_LIMITS.description} caracteres.`
                : `${form.description.length}/${ACCOUNT_FIELD_LIMITS.description} caracteres.`}
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Switch checked={form.active} onChange={(event) => setForm((current) => ({ ...current, active: event.target.checked }))} />
              <Typography>Cuenta activa</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? "Guardando..." : "Guardar cuenta"}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
